import React, { useEffect, forwardRef, useRef } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { LoaderCircle } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { CloseIcon } from 'components/icons/v2';
import { useUrlMetadata } from 'hooks/useUrlMetadata';
import { ProudOfItem } from '../../_components/CV/_components/ProudOf';

interface ProudOfItemFieldProps extends ControllerRenderProps<any, string> {
  onRemove: () => void;
  urlError?: string;
}

export const ProudOfItemField = forwardRef<HTMLDivElement, ProudOfItemFieldProps>(({
  onRemove,
  urlError,
  value,
  onChange,
}, forwardedRef) => {
  const { metadata, loading, error, fetchMetadata, isValidUrl } = useUrlMetadata();
  const originalUrlRef = useRef(value.url);
  
  useEffect(() => {
    if (metadata) {
      onChange({
        ...value,
        ...metadata
      });
    }
  }, [metadata, onChange]);

  const handleUrlChange = (url: string) => {
    const newValue = { ...value };
    newValue.url = url;
    
    // If URL changed from original, fetch new metadata
    if (url !== originalUrlRef.current && isValidUrl(url)) {
      fetchMetadata(url);
    }
    
    onChange(newValue);
  };

  return (
    <div className="flex flex-col gap-3">
      {/* URL Input */}
      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            placeholder="https://example.com - Enter URL of your achievement"
            errorText={urlError}
            className={'w-full'}
            containerClassName={'overflow-visible'}
            value={value.url || ''}
            onChange={(e) => handleUrlChange(e.target.value)}
          />
        </div>
        <Button
          size='x-small'
          variant='nakedBrandLink'
          type='button'
          onClick={onRemove}
          className='!p-0 text-gray-400 flex-shrink-0 tltp-wrapper'
        >
          <div className={'tltp-content'}>Remove</div>
          <CloseIcon width={20} height={20} className={'text-gray-400 hover:text-gray-600'} />
        </Button>
      </div>

      {/* URL Metadata Preview */}
      {value.url && (
        <div className="bg-gray-50 relative">
          {loading && (
            <div className="absolute inset-0 bg-gray-100/60 flex items-center justify-center z-10">
              <div className="flex items-center gap-2 text-gray-500">
                <LoaderCircle className="animate-spin w-4 h-4" />
                <span className="text-sm">Fetching URL data...</span>
              </div>
            </div>
          )}
          
          <ProudOfItem 
            item={{
              ...value,
              description: error ? error : value.description || value.url
            }}
          />
        </div>
      )}
    </div>
  );
});

ProudOfItemField.displayName = 'ProudOfItemField';
